import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
// import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { PermissionsService } from '@app/modules/auth';
import { UserProfile } from '@app/modules/auth/models//core-user-api.model';
import { of, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { SidenavConfig } from '../config/sidenav-config';
import { SidenavFooter, SidenavHeader, SidenavMenu, SidenavParams } from './interfaces';
import { CookieName } from '../consts/cookie.const';
import { defaultSidenavCssParams, DefaultSidenavParams } from '../consts/sidenav.const';
import { cloneDeep } from 'lodash';
import { MAT_MENU_DEFAULT_OPTIONS } from '@angular/material/menu';
import { getCookie, setCookie } from '../utils';
import { VdoUser } from '@app/modules/auth/models/vdo-user';
import { Company } from '../models/company.model';
import { MapSettings } from '@app/modules/location/models/settings.model';
import { SettingsApiService } from '@app/services/settings-api.service';
import { LocationFacade } from '@app/modules/location/facade/location.facade';

@Component({
  selector: 'zui-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  providers: [
    {
      provide: MAT_MENU_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: 'sidenav-overlay-popover' }
    }
  ]
})
export class SidenavComponent implements OnInit, OnChanges, OnDestroy {
  @Input() sidenavParams: SidenavParams = {};
  @Input() header?: SidenavHeader;
  @Input() menu?: SidenavMenu[];
  @Input() footer?: SidenavFooter;
  @Input() accountId: string;
  @Output() toggled: EventEmitter<boolean> = new EventEmitter<boolean>();
  /* emit the selected company up to the chain to expose it to the applications */
  @Output() menuAction: EventEmitter<string> = new EventEmitter<string>();

  selectedCompany = null;
  isSingleCompanyUser = false;
  readonly width = '32.5rem';
  readonly panelClass = 'change-company-dialog';
  isMobileView = false;
  mobileViewInitialized = false;
  userDesktopMenuOpen = false;
  subscriptions: Subscription[] = [];
  /* logged in user information from Auth0 */
  user: VdoUser;
  userProfile: UserProfile;
  isLoggedIn: boolean;
  rebuiltMenuConfig: SidenavMenu[] = [];
  currentUrl: string = '';
  selectedMenuItem: SidenavMenu;
  // save state of rebuiltMenuConfig
  cacheMenuConfigs: SidenavMenu[] = [];
  toggledExpandableMenu: SidenavMenu;
  parentsPath: SidenavMenu[] = [];
  /**
   * key: string: name of item matching with url
   * value: SidenavMenu[]:  parents of item matching
   */
  parentsPathByItemsMatchingMap = {};
  allowedApplications = null;
  windowComponent = window;
  isSideNavInitialized = false;

  constructor(
    private router: Router,
    // private dialog: MatDialog,
    public media: MediaObserver,
    private breakpointObserver: BreakpointObserver,
    private auth: AuthService,
    private permissionsService: PermissionsService,
    private readonly settingsService: SettingsApiService,
    private sidenavConfig: SidenavConfig,
    private locationFacade: LocationFacade,
    @Inject('logoutReturnToUrl') private readonly logoutReturnToUrl: string,
    @Inject('applicationId') private readonly applicationId: string
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.menu && changes.menu.currentValue && !changes.menu.firstChange) {
      if (this.allowedApplications) {
        this.buildMenuConfig(this.allowedApplications);
      } else {
        this.subscribeToUser();
      }
    }
  }

  ngOnInit(): void {
    this.initSideNav();
  }

  initSideNav() {
    this.initSidenavParams();
    this.sidenavConfig.isSideNavInitialized.subscribe(isInitialized => {
      if (isInitialized) {
        this.isSideNavInitialized = isInitialized;
        this.initSidenavConfig();
        this.initHeaderConfig();
        this.initFooterConfig();
        this.subscribeToData();
        this.subscribeToBreakpoints();
        this.subscribeToRouter();
      }
    });
  }

  /**
   * Function to assign default values to sidenavParams
   */
  initSidenavParams() {
    Object.keys(DefaultSidenavParams).forEach(paramKey => {
      if (!this.sidenavParams.hasOwnProperty(paramKey)) {
        this.sidenavParams[paramKey] = DefaultSidenavParams[paramKey];
      }
    });
  }

  subscribeToRouter() {
    this.subscriptions.push(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
        this.currentUrl = event.urlAfterRedirects;
        if (this.currentUrl) {
          this.highlightMatchingUrlMenuItem(this.currentUrl);
        }
      })
    );
  }

  highlightAppItem(arrayItems: SidenavMenu[]) {
    if (arrayItems?.length) {
      const routerPath = this.extractRouterPath(this.currentUrl);
      for (const item of arrayItems) {
        if (item.appId === this.applicationId && (routerPath === '' || routerPath === item.route)) {
          item.isSelected = true;
          return;
        } else {
          this.highlightAppItem(item.children);
        }
      }
    }
  }

  extractRouterPath(input: string): string {
    const regex = /\/([^\/]+)\/?$/;
    const match = input.match(regex);
    if (match) {
      return match[1];
    }
    return '';
  }

  /**
   * Function to check the menu configs to use either app to highlight or URL to highlight.
   * If the current matching app has no children then it will return true.
   * If all menu items is an app and has no children then it is true.
   * Otherwise it return false.
   * @returns true to use app highlight, false to use URL highlight.
   */
  checkAppItems(): boolean {
    let count = 0;
    for (const item of this.rebuiltMenuConfig) {
      if (item.appId === this.applicationId && !item.children?.length) {
        return true;
      }

      if (
        (item.appId && !item.children?.length) ||
        (!item.appId && !item.route && item.children.every(i => !i.children?.length))
      ) {
        count++;
      }
    }

    return count === this.rebuiltMenuConfig.length;
  }

  /**
   * Function to highlight menu item that matches URL/appId based on the menu configs.
   */
  highlightMatchingUrlMenuItem(url: string) {
    if (!this.rebuiltMenuConfig.length) {
      return;
    }

    // Reset selected state of all items from cache menu
    this.rebuiltMenuConfig = cloneDeep(this.cacheMenuConfigs);
    if (this.checkAppItems()) {
      // Highlight by AppId
      this.highlightAppItem(this.rebuiltMenuConfig);
    } else {
      // Highlight by URL
      const urlParts = url?.split('/');
      this.parentsPathByItemsMatchingMap = {};
      if (urlParts?.length) {
        urlParts.forEach(url => {
          if (url) {
            this.selectedMenuItem = null;
            this.parentsPath = [];
            this.getMatchingUrlMenuItemGroup(this.rebuiltMenuConfig, url);
          }
        });
        this.highlightMenuItemGroup();
      }
    }
  }

  /**
   * Function to choose the correct selected menu item from the url part and populate the parentsPath to keep the selected menu item parents for highlight.
   * parentsPath is to track the path from root item to the matching item. We will use this one to expand the parent items until we show the matching item.
   * @param menu The menu configs array
   * @param url The url part to compare with item's route
   * @returns void
   */
  getMatchingUrlMenuItemGroup(menu: SidenavMenu[], url: string): void {
    if (menu?.length) {
      for (const menuItem of menu) {
        this.parentsPath.push(menuItem);
        if (menuItem.route === url) {
          this.selectedMenuItem = menuItem;
        } else {
          this.getMatchingUrlMenuItemGroup(menuItem.children, url);
        }
        if (this.selectedMenuItem && Object.keys(this.selectedMenuItem).length) {
          this.parentsPathByItemsMatchingMap[this.selectedMenuItem.text] = this.parentsPath;
          return;
        }
        this.parentsPath.pop();
      }
    }
  }

  /**
   * Function to determine the matching selected item and its parentsPath.
   * @returns the root parent of the selected item.
   */
  findParentSelectedItem(): SidenavMenu {
    let parent: SidenavMenu;
    const keys = Object.keys(this.parentsPathByItemsMatchingMap);
    if (keys.length) {
      this.selectedMenuItem = this.parentsPathByItemsMatchingMap[keys[0]][0];
      parent = this.parentsPathByItemsMatchingMap[keys[0]][0];
      keys.forEach((key: string) => {
        this.parentsPathByItemsMatchingMap[key].forEach(item => {
          if (this.selectedMenuItem.routeLevel < item.routeLevel) {
            this.selectedMenuItem = item;
            parent = this.parentsPathByItemsMatchingMap[key][0];
          }
        });
      });
    }

    return parent;
  }

  /**
   * Function to apply highlight.
   * It will expand all parent items to matching item.
   * It will set the highlight attributes for related items.
   * isSelected is applied on the selected item.
   * isSecondarySelected is applied on all other items in the same group of the selected item, including the parent.
   */
  highlightMenuItemGroup() {
    const parentItemSelected = this.findParentSelectedItem();
    if (parentItemSelected) {
      let root = Object.values(this.rebuiltMenuConfig).find(item => item.text === parentItemSelected.text);
      if (root && root.children?.length) {
        root.isSecondarySelected = true;
        root.isExpanded = true;
        this.highlightMenuItems(root.children);
      } else if (root) {
        root.isSelected = true;
      }
    }

    if (Object.keys(this.parentsPathByItemsMatchingMap).length && this.selectedMenuItem) {
      // Check expanded parent item
      this.parentsPathByItemsMatchingMap[this.selectedMenuItem.text]?.forEach(item => {
        this.expandedItems(this.rebuiltMenuConfig, item);
        // Update cache menu
        this.expandedItems(this.cacheMenuConfigs, item);
      });
    }
  }

  /**
   * Function to apply expand on arrayItems item base on the input itemExpanded text.
   * @param arrayItems
   * @param itemExpanded
   */
  expandedItems(arrayItems, itemExpanded) {
    if (arrayItems?.length) {
      arrayItems.forEach(item => {
        if (item.text === itemExpanded.text) {
          item.isExpanded = true;
        } else {
          this.expandedItems(item.children, itemExpanded);
        }
      });
    }
  }

  /**
   * Function to apply highlight on menuItems array based on the selected menu item
   * @param menuItems
   */
  highlightMenuItems(menuItems: SidenavMenu[]) {
    if (menuItems?.length) {
      menuItems.forEach((item: SidenavMenu) => {
        if (item.text === this.selectedMenuItem.text) {
          item.isSelected = true;
        } else {
          item.isSecondarySelected = true;
        }
        this.highlightMenuItems(item.children);
      });
    }
  }

  initSidenavConfig() {
    const sidenavConfigCookie = JSON.parse(getCookie(CookieName.SIDENAV_CONFIG));
    if (sidenavConfigCookie.hasOwnProperty('expanded')) {
      this.initSidenavExpandConfig(sidenavConfigCookie.expanded);
    }
    if (!this.sidenavParams.expanded) {
      this.rebuiltMenuConfig?.forEach(m => (m.isExpanded = false));
    }
  }

  initSidenavExpandConfig(sidenavExpandConfigCookieValue) {
    this.sidenavParams.expanded = sidenavExpandConfigCookieValue;
  }

  initHeaderConfig() {
    if (!this.header) {
      this.header = this.sidenavConfig.getHeaderConfig();
    }
  }

  initFooterConfig() {
    if (!this.footer) {
      this.footer = this.sidenavConfig.getFooterConfig();
    }

    if (this.sidenavParams.useDefaultHelpItem) {
      this.footer.items.push(this.sidenavConfig.getDefaultHelpItemItem());
    }
  }

  subscribeToAuth() {
    return this.auth.user$.pipe(filter(profile => profile != null)).subscribe(() => {
      this.isLoggedIn = true;
    });
  }

  subscribeToUser() {
    return this.permissionsService
      .getUser()
      .pipe(
        map((vdoUser: VdoUser) => {
          if (!vdoUser || !Object.keys(vdoUser).length) {
            return throwError('No User Found!');
          }
          this.user = vdoUser;
          //const userProfileParams: GetUserProfilesParams = { userId: ""+vdoUser.id, status: 'ACTIVE', per_page: '5000' };

          //return this.coreUserApiService.getUserProfiles(userProfileParams);
          return this.permissionsService.createDefaultUserProfile(vdoUser);
        }),
        switchMap((userProfile: UserProfile) => {
          if (!userProfile || !Object.keys(userProfile).length) {
            return throwError('No User Profiles Found!');
          }
          this.allowedApplications = [userProfile.applicationId]; // Array.from(new Set(userProfile.map(profile => profile.applicationId)));
          // init menu config after we have profiles information
          this.buildMenuConfig(this.allowedApplications);
          return of();
        }),
        catchError(err => {
          console.log('Sidenav Permission service get user error', err);
          return of();
        })
      )
      .subscribe();
  }

  buildMenuConfig(allowedApplications) {
    if (!this.menu) {
      this.menu = this.sidenavConfig.getMenuConfig();
    }
    this.rebuiltMenuConfig = !Object.keys(this.menu).length
      ? []
      : this.menu.filter(menuItem => this.recursiveFilterRestrictedMenuItem(menuItem, allowedApplications));

    this.cacheMenuConfigs = cloneDeep(this.rebuiltMenuConfig);
    this.highlightMatchingUrlMenuItem(this.currentUrl);
  }

  recursiveFilterRestrictedMenuItem(menuItem: SidenavMenu, applications: any[]) {
    if (this.filterRestrictedMenuItem(menuItem, applications)) {
      if (menuItem?.children?.length) {
        menuItem.children = menuItem.children.filter(menuChild =>
          this.recursiveFilterRestrictedMenuItem(menuChild, applications)
        );
      }
      return true;
    }
    return false;
  }

  filterRestrictedMenuItem(menuItem: SidenavMenu, applications: any[]) {
    if (menuItem.appId) {
      return applications.includes(menuItem.appId);
    }
    return true;
  }

  subscribeToProfile() {
    return this.permissionsService.userProfiles$
      .pipe(
        withLatestFrom(this.permissionsService.isZonarUser$),
        take(1),
        switchMap(([profiles, isZonarUser]) => {
          // this.isZonarUser = isZonarUser;
          const companies = Array.from(new Set(profiles?.map(profile => profile.companyId)));
          const isMultiCompany = isZonarUser || companies.length > 1;
          if (isMultiCompany) {
            this.isSingleCompanyUser = false;
          } else {
            this.isSingleCompanyUser = true;
          }
          return of(profiles);
        })
      )
      .subscribe(profiles => {
        this.userProfile = profiles?.length ? profiles[0] : null;
      });
  }

  subscribeToAccount() {
    return this.permissionsService
      .getCurrentCompany()
      .pipe(filter((company: Company) => !!company?.id))
      .subscribe((company: Company) => {
        this.selectedCompany = { title: company?.name };
      });
  }

  subscribeToData() {
    this.subscriptions = [
      this.subscribeToUser(),
      this.subscribeToAuth(),
      this.subscribeToProfile(),
      this.subscribeToAccount()
    ];
  }

  openDrawer(e: Event) {
    e.stopPropagation();
    this.sidenavParams.expanded = true;
    this.userDesktopMenuOpen = false;
    this.setSidenavConfigCookie();
  }

  closeDrawer(e: Event) {
    e.stopPropagation();
    this.sidenavParams.expanded = false;
    this.userDesktopMenuOpen = false;
    this.rebuiltMenuConfig?.forEach(m => (m.isExpanded = false));
    this.setSidenavConfigCookie();
  }

  setSidenavConfigCookie() {
    const userSidenavConfigCookieString = JSON.stringify({
      expanded: this.sidenavParams.expanded
    });
    setCookie(CookieName.SIDENAV_CONFIG, userSidenavConfigCookieString);
  }

  onMenuItemClicked(menu: SidenavMenu) {
    if (menu?.icon == 'dashboard') {
      sessionStorage.removeItem('routeActivation');
      sessionStorage.removeItem('demoMode');
      sessionStorage.removeItem('assetVINsMap');
    }
    if (menu?.routerLink) {
      this.toggleOpenSidenav(false);
      this.navigateTo(menu.routerLink);
    } else {
      this.menuAction.emit(menu?.text);
    }
  }

  onParentMenuItemClicked(parentItem: SidenavMenu) {
    if (!this.sidenavParams.expanded) {
      return;
    }
    parentItem.isExpanded = !parentItem.isExpanded;
    this.toggledExpandableMenu = null;
    this.saveStateToCacheMenuConfigs(parentItem.text, this.cacheMenuConfigs, parentItem.isExpanded);
  }

  /**
   * Function to save the expand state of the menu configs to cache menu configs.
   * @param searchText Text of the item
   * @param searchMenu The cache menu to store the state
   * @param isExpanded The current expanded state of the item
   * @returns void
   */
  saveStateToCacheMenuConfigs(searchText: string, searchMenu: SidenavMenu[], isExpanded: boolean): void {
    for (const menuItem of searchMenu) {
      if (menuItem.children?.length && menuItem.text === searchText) {
        menuItem.isExpanded = isExpanded;
        this.toggledExpandableMenu = menuItem;
      } else if (menuItem.children?.length) {
        this.saveStateToCacheMenuConfigs(searchText, menuItem.children, isExpanded);
      }
      if (this.toggledExpandableMenu && Object.keys(this.toggledExpandableMenu).length) {
        return;
      }
    }
  }

  onFooterItemClick(item: SidenavMenu) {
    if (item?.routerLink) {
      this.navigateTo(item.routerLink);
    }
  }

  navigateTo(path: string) {
    const externalLinkPrefix = 'http';
    /* external link */
    if (path.toLocaleLowerCase().startsWith(externalLinkPrefix)) {
      this.windowComponent.location.href = path;
    } else {
      /* internal link */
      this.router.navigate([path]);
    }
  }

  // setSelectedCompany(company) {
  //   this.selectedCompany = company;
  //   this.changeCompanyService.setSelectedCompany(company);
  //   /* emit the selected company up to the chain to expose it to the applications */
  //   this.emitSelectedCompany.emit(this.selectedCompany);
  //   if (company) {
  //     this.toggleOpenSidenav(false);
  //   } else {
  //     this.toggleOpenSidenav(true);
  //   }
  // }

  // openChangeCompanyDialog(hasDefaultCompany: boolean): void {
  //   if (this.dialog.openDialogs && this.dialog.openDialogs.length) {
  //     return;
  //   }
  // }

  subscribeToBreakpoints() {
    // We decided to use a min breakpoint, t is also possible to implement specific sizes e.g.
    // this.breakpointObserver.observe([
    //   Breakpoints.Handset, ...
    // ])

    this.breakpointObserver
      .observe(['(min-width: ' + this.sidenavParams.mobileViewWidthBreakpoint + 'rem)'])
      .subscribe((state: BreakpointState) => {
        if (state.matches) {
          this.isMobileView = false;
          this.userDesktopMenuOpen = false;
        } else {
          this.isMobileView = true;
          this.userDesktopMenuOpen = false;
          if (!this.mobileViewInitialized) {
            /* force to close sidenav in mobile as a default */
            this.toggleOpenSidenav(false);
          }
        }
        this.mobileViewInitialized = true;
      });
  }

  // function to toggle open/close sidenav mobile if its not open/close
  toggleOpenSidenav(sidenavOpen: boolean) {
    if (this.sidenavParams.mobileOpened !== sidenavOpen) {
      this.sidenavParams.mobileOpened = sidenavOpen;
      this.toggled.emit(this.sidenavParams.mobileOpened);
    }
  }

  onSideNavOpened(event) {
    // console.log('onSideNavOpened');
  }

  onSideNavClosed(event) {
    // console.log('onSideNavClosed');
  }

  onSideNavBackdropClicked(event) {
    this.toggleOpenSidenav(false);
    this.userDesktopMenuOpen = false;
  }

  onSidenavContentClicked() {
    this.userDesktopMenuOpen = false;
  }

  onCloseSidenavClicked(event) {
    this.toggleOpenSidenav(false);
  }

  onAvatarClicked(event) {
    /* if the sidenav is not in mobile view and collapsed; display a user menu */
    if (!this.isMobileView && !this.sidenavParams.expanded) {
      this.userDesktopMenuOpen = !this.userDesktopMenuOpen;
    }
  }

  onLogoutClick() {
    this.isLoggedIn ? this.auth.logout({ returnTo: this.logoutReturnToUrl }) : this.auth.loginWithRedirect();
  }

  recursiveCallActionMenuItem(menuItem: SidenavMenu, action: any, actionParams = {}) {
    action(menuItem, actionParams);
    if (menuItem?.children?.length) {
      menuItem.children.forEach(menuChild => {
        this.recursiveCallActionMenuItem(menuChild, action, actionParams);
      });
    }
  }

  getSidenavContainerClasses() {
    let containerClasses = '';
    if (this.isMobileView) {
      containerClasses = 'mobile-mode';
      if (!this.sidenavParams.mobileOpened) {
        containerClasses += ' collapsed-state';
      }
    } else {
      containerClasses = 'desktop-mode';
      if (!this.sidenavParams.expanded) {
        containerClasses += ' collapsed-state';
      }
    }

    if (this.isLoggedIn || this.sidenavParams.displayToAnonymousUser) {
      containerClasses += ' block-display';
    } else {
      containerClasses += ' none-display';
    }

    return containerClasses;
  }

  // Formula: result = this.maxWidth - iconWidth - (deep * level) - expandDownIconWidth
  getParentMenuMaxWidth(depthMultiplier: number) {
    let textWidth = defaultSidenavCssParams.MAX_WIDTH_EXPAND;
    textWidth =
      textWidth -
      defaultSidenavCssParams.MENU_ICON -
      depthMultiplier * defaultSidenavCssParams.PADDING_PER_CHILD -
      defaultSidenavCssParams.EXPAND_MENU_ICON -
      defaultSidenavCssParams.BUTTON_PADDING_RIGHT +
      0.635;

    return { 'max-width': textWidth + 'rem' };
  }

  getNestedMenuMaxWidth(depthMultiplier: number, hasMenuIcon: boolean) {
    let textWidth = defaultSidenavCssParams.MAX_WIDTH_EXPAND;
    textWidth =
      textWidth -
      (hasMenuIcon ? defaultSidenavCssParams.MENU_ICON : 0) -
      depthMultiplier * defaultSidenavCssParams.PADDING_PER_CHILD -
      defaultSidenavCssParams.BUTTON_PADDING_RIGHT +
      0.635;

    return { 'max-width': textWidth + 'rem' };
  }

  getSidenavContentMargin() {
    if (this.isMobileView || !this.sidenavParams.enableContentResizing) {
      return null;
    }
    let sidenavWidth = '';
    let sidenav = document.querySelector('.sidenav-inner-container');
    sidenavWidth = getComputedStyle(sidenav).width;

    return { width: `calc(100vw - ${sidenavWidth})`, 'margin-left': sidenavWidth };
  }

  /**
   * Function to check if the item has any child or nested child that is selected
   * @param parentItem the parent item that will be checked
   * @returns boolean
   */
  checkChildrenSelected(parentItem: SidenavMenu): boolean {
    if (!parentItem?.children?.length) {
      return false;
    }
    for (const item of parentItem.children) {
      if (item.isSelected) {
        return true;
      }
      const result = this.checkChildrenSelected(item);
      if (result) {
        return true;
      }
    }
    return false;
  }

  /**
   * Function to get an array of style classes for the menu item to highlight correctly.
   * If the item is selected, deep color is applied for its background.
   * If a root item is either selected or secondary selected, a marker is also added.
   * If a parent is secondary selected and its child is selected, has-child-selected class will be applied.
   */
  getMenuItemStyleClasses(menuItem: SidenavMenu, itemDepth: number) {
    const styleClasses = [];
    if (menuItem?.isSelected) {
      styleClasses.push('menu-item-selected');
    }
    if (menuItem?.isSecondarySelected) {
      if (this.checkChildrenSelected(menuItem)) {
        styleClasses.push('menu-item-secondary-selected has-child-selected');
      } else {
        styleClasses.push('menu-item-secondary-selected');
      }
    }
    if (itemDepth === 0 && (menuItem?.isSelected || menuItem?.isSecondarySelected)) {
      styleClasses.push('menu-item-marker');
    }
    return styleClasses;
  }

  // Because ngClass doesn't apply additional classes to mat-menu,
  // need to convert to string and add via [class] input
  getMenuItemStyleClassesAsString(menuItem: SidenavMenu) {
    return this.getMenuItemOverlayStyleClasses(menuItem).join();
  }

  getMenuItemOverlayStyleClasses(menuItem: SidenavMenu) {
    const styleClasses = [];
    if (menuItem?.isSelected) {
      styleClasses.push('menu-item-selected');
    }
    if (menuItem?.isSecondarySelected) {
      styleClasses.push('menu-item-secondary-selected');
    }
    return styleClasses;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
